import React from "react";
import { Box, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";

const EVENT_CONFIG = {
  create:   { icon: AddCircleOutlineIcon,   color: "#4caf50", label: (e) => "Ticket criado" },
  accept:   { icon: CheckCircleOutlineIcon, color: "#1976d2", label: (e) => `Aceito por ${e.userName || "atendente"}` },
  transfer: { icon: SwapHorizIcon,          color: "#ff9800", label: (e) => {
    const d = e.additionalData || {};
    return d.toQueueName ? `Transferido para ${d.toQueueName}` : "Transferido";
  }},
  update:   { icon: PauseCircleOutlineIcon, color: "#9e9e9e", label: (e) => {
    const d = e.additionalData || {};
    if (d.previousStatus === "open") return "Movido para aguardando";
    if (d.previousStatus === "pending") return "Reaberto";
    return e.description || "Atualizado";
  }},
  reopen:   { icon: ReplayIcon,             color: "#4caf50", label: (e) => `Reaberto por ${e.userName || "atendente"}` },
  close:    { icon: CancelOutlinedIcon,     color: "#f44336", label: (e) => `Encerrado por ${e.userName || "atendente"}` },
  protocol: { icon: AssignmentIcon,         color: "#7b1fa2", label: (e) => {
    const num = e.additionalData?.protocolNumber || e.description?.replace("Protocolo ", "") || e.description;
    return num;
  }},
};

const TicketEventItem = ({ event }) => {
  const time = new Date(event.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  if (event.action === "note") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 1, px: 2 }}>
        <Box sx={{
          backgroundColor: "#fff9c4",
          border: "1px solid #f9a825",
          borderRadius: 1,
          px: 2, py: 1,
          maxWidth: "75%",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}>
          <Typography variant="caption" sx={{ color: "#f57f17", fontWeight: 600, display: "block", mb: 0.5 }}>
            📌 Nota interna — {event.userName || "Sistema"} · {time}
          </Typography>
          <Typography variant="body2" sx={{ color: "#333", whiteSpace: "pre-wrap", fontSize: "0.8rem" }}>
            {event.description}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (event.action === "task") {
    const d = event.additionalData || {};
    const mins = Math.round((d.durationSeconds || 0) / 60);
    const period = d.startAt && d.endAt
      ? `${new Date(d.startAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} → ${new Date(d.endAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`
      : null;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 1, px: 2 }}>
        <Box sx={{
          backgroundColor: "#e3f2fd",
          border: "1px solid #64b5f6",
          borderRadius: 1,
          px: 2, py: 1,
          maxWidth: "75%",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}>
          <Typography variant="caption" sx={{ color: "#1565c0", fontWeight: 600, display: "block", mb: 0.5 }}>
            ⏱️ Tarefa registrada — {event.userName || "Sistema"} · {time}
          </Typography>
          <Typography variant="body2" sx={{ color: "#333", whiteSpace: "pre-wrap", fontSize: "0.8rem" }}>
            {event.description}
          </Typography>
          <Typography variant="caption" sx={{ color: "#1976d2", fontSize: "0.7rem", display: "block", mt: 0.5 }}>
            {period ? `${period} · ` : ""}{Math.floor(mins / 60)}h{String(mins % 60).padStart(2, "0")}min
          </Typography>
        </Box>
      </Box>
    );
  }

  if (event.action === "protocol") {
    const num = event.additionalData?.protocolNumber || event.description?.replace("Protocolo ", "") || event.description;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 1, px: 2 }}>
        <Box sx={{
          backgroundColor: "#f3e5f5",
          border: "1px solid #ce93d8",
          borderRadius: 1,
          px: 2, py: 0.75,
          display: "flex", alignItems: "center", gap: 1,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}>
          <AssignmentIcon sx={{ fontSize: 16, color: "#7b1fa2" }} />
          <Typography variant="caption" sx={{ color: "#4a148c", fontWeight: 700, fontSize: "0.75rem" }}>
            Protocolo registrado:
          </Typography>
          <Typography variant="caption" sx={{ color: "#6a1b9a", fontWeight: 600, fontSize: "0.75rem", fontFamily: "monospace" }}>
            {num}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9c4dcc", fontSize: "0.65rem", ml: 0.5 }}>
            · {time}
          </Typography>
        </Box>
      </Box>
    );
  }

  const cfg = EVENT_CONFIG[event.action] || {
    icon: CheckCircleOutlineIcon, color: "#9e9e9e",
    label: (e) => e.description || e.action,
  };
  const Icon = cfg.icon;
  const label = cfg.label(event);

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, my: 1, px: 2 }}>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.5,
        backgroundColor: "background.paper",
        border: "1px solid", borderColor: "divider",
        borderRadius: 4, px: 1.25, py: 0.4,
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
      }}>
        <Icon sx={{ fontSize: 13, color: cfg.color }} />
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", lineHeight: 1.4 }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.65rem", ml: 0.5 }}>
          {time}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "divider" }} />
    </Box>
  );
};

export default TicketEventItem;
